import { Grid2, Typography, IconButton, Chip, Tooltip, Box, useTheme, useMediaQuery, Button } from "@mui/material";
import { icon } from "@/app/style/icons";
import EmailIcon from "@mui/icons-material/Email";
import { RouteTo } from "@routing/RouteMap";
import { UserSize } from "./UserManagementRow";

type InviteLink = {
    id: string;
    code: string;
    email: string;
    role: string;
    size: UserSize;
    isUsed: boolean;
    createdAt: string;
};

interface InviteLinkRowProps {
    inviteLink: InviteLink;
    onDeleteClick: (inviteLink: InviteLink) => void;
    onSwapClick?: (inviteLink: InviteLink) => void;
    sizeColor?: string;
}

export const InviteLinkRow = ({ inviteLink, onDeleteClick, onSwapClick, sizeColor }: InviteLinkRowProps) => {
    // Format the date to be more readable
    const formattedDate = new Date(inviteLink.createdAt).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    // Mobile layout for small screens
    if (isSmallScreen) {
        return (
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    p: 2,
                    "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                    },
                    borderRadius: 1,
                    border: 1,
                    borderColor: sizeColor || "divider",
                    bgcolor: "rgba(0,0,0,0.01)",
                }}
            >
                {/* Top row: Icon, Email, Copy and Delete buttons */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {/* Icon */}
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            bgcolor: "rgba(0,150,136,0.1)",
                            mr: 1.5,
                            flexShrink: 0,
                        }}
                    >
                        <EmailIcon color="secondary" />
                    </Box>

                    {/* Email and date */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" noWrap>
                            {inviteLink.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontSize={12}>
                            Erstellt: {formattedDate}
                        </Typography>
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{ display: "flex", ml: 1 }}>
                        {/* Copy button */}
                        <IconButton
                            size="small"
                            onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}${RouteTo.INVITE}/${inviteLink.code}`);
                            }}
                            title="Link kopieren"
                            sx={{ mr: 0.5 }}
                        >
                            <icon.copy sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>

                        {/* Delete button */}
                        <Tooltip title={inviteLink.isUsed ? "Verwendete Einladungen können nicht gelöscht werden" : undefined} placement="top">
                            <span>
                                <IconButton size="small" disabled={inviteLink.isUsed} onClick={() => !inviteLink.isUsed && onDeleteClick(inviteLink)}>
                                    <icon.delete sx={{ color: "semantic.icon.functional" }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Bottom row: Role, Size, Status chip */}
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
                    {/* Role and Size in one column */}
                    <Box sx={{ display: "flex", gap: 3, mr: 2 }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                Rolle
                            </Typography>
                            <Typography variant="body2">{inviteLink.role}</Typography>
                        </Box>

                        <Box>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                Größe
                            </Typography>
                            <Typography variant="body2">{inviteLink.size || "S"}</Typography>
                        </Box>
                    </Box>

                    {/* Status chip */}
                    <Chip
                        label={inviteLink.isUsed ? "Verwendet" : "Ausstehend"}
                        color={inviteLink.isUsed ? "default" : "success"}
                        variant={inviteLink.isUsed ? "outlined" : "filled"}
                        size="small"
                        sx={{ background: sizeColor }}
                    />
                </Box>
            </Box>
        );
    }

    // Desktop layout
    return (
        <Grid2
            container
            alignItems="center"
            size={12}
            spacing={2}
            sx={{
                width: "100%",
                maxWidth: "100%",
                p: 2,
                "&:hover": {
                    bgcolor: "rgba(0,0,0,0.02)",
                },
                borderRadius: 1,
                border: 1,
                borderColor: sizeColor || "divider",
                bgcolor: "rgba(0,0,0,0.01)", // slightly different background to distinguish from users
                display: "flex",
                flexWrap: "nowrap",
                boxSizing: "border-box",
            }}
        >
            {/* Icon (instead of Avatar) */}
            <Grid2 sx={{ flexShrink: 0 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        bgcolor: "rgba(0,150,136,0.1)",
                    }}
                >
                    <EmailIcon color="secondary" />
                </Box>
            </Grid2>

            {/* Email and Status */}
            <Grid2 display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{ minWidth: 150, flexShrink: 1, flexGrow: 1, maxWidth: 220 }}>
                <Grid2>
                    <Typography variant="subtitle2" noWrap>
                        {inviteLink.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={12}>
                        Erstellt: {formattedDate}
                    </Typography>
                </Grid2>

                {/* Copy button for invite link */}
                <Grid2 height={"100%"} display="flex" alignItems="center">
                    <IconButton
                        size="small"
                        onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}${RouteTo.INVITE}/${inviteLink.code}`);
                        }}
                        title="Link kopieren"
                    >
                        <icon.copy sx={{ color: "semantic.icon.functional" }} />
                    </IconButton>
                </Grid2>
            </Grid2>

            {/* Rolle */}
            <Grid2 display="flex" alignItems="center" sx={{ flexShrink: 0, minWidth: 80 }}>
                <Typography variant="body2" color="text.secondary">
                    {inviteLink.role}
                </Typography>
            </Grid2>

            {/* Status chip (where quota would be) */}
            <Grid2 ml={4} flexGrow={1} display="flex" justifyContent="flex-end" sx={{ flexShrink: 0, minWidth: 100 }}>
                <Chip
                    label={inviteLink.isUsed ? "Verwendet" : "Ausstehend"}
                    color={inviteLink.isUsed ? "default" : "success"}
                    variant={inviteLink.isUsed ? "outlined" : "filled"}
                    size="small"
                    sx={{ background: sizeColor }}
                />
                {/*<Button variant={"outlined"}>{inviteLink.isUsed ? "Verwendet" : "Ausstehend"}</Button>*/}
            </Grid2>

            <Grid2>
                <IconButton
                    onClick={() => onSwapClick?.(inviteLink)}
                    disabled={inviteLink.isUsed || !onSwapClick}
                >
                    <icon.switch />
                </IconButton>
            </Grid2>

            {/* Delete Button */}
            <Grid2 display="flex" alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
                <Tooltip title={inviteLink.isUsed ? "Verwendete Einladungen können nicht gelöscht werden" : undefined} placement="top">
                    <span>
                        {" "}
                        {/* Wrapper needed for disabled buttons */}
                        <IconButton size="small" disabled={inviteLink.isUsed} onClick={() => !inviteLink.isUsed && onDeleteClick(inviteLink)}>
                            <icon.delete sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Grid2>
        </Grid2>
    );
};
